/** Roles, presets and the lever registry (`roles.*` levers). Shapes copied from workers/api/src/roles. */
import type { BasisPoints, LeverId, Scope } from "@helmr/contracts";
import type { ApiClient } from "./client";

export interface RoleRecord {
  id: string; name: string; isOwner: boolean; presetFrom: string | null;
  scope: Scope;
  /** Effective levers, sorted. The owner's list holds every lever. */
  levers: LeverId[];
  /** Registry version the role was last saved against; `null` only for the owner. */
  leverRegistryVersion: number | null;
  discountCapBp: BasisPoints;
  version: number; createdAt: number; updatedAt: number; updatedBy: string | null;
  grantsInvalid?: true;
}
/** `GET /roles/levers` (D57): the registry as the server holds it. The screen renders this, never a bundled copy. */
export interface LeverNode { id: LeverId; label: string; hint: string; requires: LeverId[]; addedIn: number; audited: boolean }
export interface GroupNode { id: string; label: string; levers: LeverNode[] }
export interface LeverTree { version: number; groups: GroupNode[] }
/** `levers` is the preset's full recommended set. */
export interface Preset { key: string; name: string; scope: Scope; discountCapBp: number; levers: LeverId[] }
export interface ResetResult { role: RoleRecord; preset: string; presetVersion: number; added: LeverId[]; removed: LeverId[] }

export interface RoleBody {
  name?: string; preset?: string; version?: number;
  /** The full lever set, not a diff. */
  levers?: LeverId[];
  scope?: Scope; discountCapBp?: number;
}

export const listRoles = (c: ApiClient) => c.get<{ roles: RoleRecord[] }>("/roles").then((r) => r.roles);
export const listPresets = (c: ApiClient) => c.get<{ presetVersion?: number; presets: Preset[] }>("/roles/presets").then((r) => r.presets);
export const getLeverTree = (c: ApiClient) => c.get<LeverTree>("/roles/levers");
export const getRole = (c: ApiClient, id: string) => c.get<{ role: RoleRecord }>(`/roles/${id}`).then((r) => r.role);
export const createRole = (c: ApiClient, body: RoleBody) => c.post<{ role: RoleRecord }>("/roles", body).then((r) => r.role);
/** `version` is required: a stale edit is a 409. */
export const patchRole = (c: ApiClient, id: string, body: RoleBody) => c.patch<{ role: RoleRecord }>(`/roles/${id}`, body).then((r) => r.role);
export const deleteRole = (c: ApiClient, id: string) => c.del<{ ok: true }>(`/roles/${id}`);
/** Sets the role's levers, scope and discount cap to a preset's current recommendation. A custom role must name `preset`. */
export const resetToRecommended = (c: ApiClient, id: string, body: { version: number; preset?: string }) =>
  c.post<ResetResult>(`/roles/${id}/reset-to-recommended`, body);
